import React, { useState, useEffect, useRef } from "react";
import { PDFDocument, rgb, StandardFonts, PDFFont } from "pdf-lib";
import toast from "react-hot-toast";
// @ts-ignore
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
// @ts-ignore
import fontkit from "@pdf-lib/fontkit";
import {
  Button,
  TextInput,
  Text,
  Stack,
  Group,
  Box,
  Paper,
  Alert,
  Select,
  Modal,
  Tabs,
  ActionIcon,
  Divider,
  Center,
  Loader,
  ThemeIcon,
  Checkbox,
  ScrollArea,
  Title,
  Drawer,
  Burger,
  Badge,
  Tooltip,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconCheck,
  IconAlertCircle,
  IconList,
  IconChevronRight,
  IconPencil,
  IconChevronLeft,
  IconChevronRight as IconChevronRight2,
  IconPageBreak,
  IconX,
  IconPlus,
  IconMinus,
  IconShieldLock,
} from "@tabler/icons-react";
import { PdfDropzone } from "./PdfDropzone";
import SignaturePad from "@uiw/react-signature";
import QRCode from "qrcode";
import WebFont from "webfontloader";
import { sha256Hex } from "../lib/hash";
import {
  createSigningMetadata,
  computeCreatorSigningInput,
  SIGNING_METADATA_FILENAME,
  type SignatureEntry,
  type SigningMetadata,
  type FieldDefinition,
} from "../lib/signing-metadata";
import { getStoredKeys, signData, verifyData, type KeyPair } from "../lib/keys";
import { verifyServerSignature } from "../lib/server-keys";
import { requestSigningNonce, requestServerAttestation } from "../lib/signing";

import { generateIntroPage } from "../lib/pdf-intro";
import { generateMetaPage } from "../lib/meta-page";
import { loadPdfTheme } from "../lib/pdf-theme";
import { renderFieldToImage, type FilledField } from "../lib/pdf-utils";

pdfjsLib.GlobalWorkerOptions.workerSrc = "./pdf.worker.js";

const SIGNER_COLORS = [
  "#228be6", // blue
  "#40c057", // green
  "#fab005", // yellow
  "#fd7e14", // orange
  "#e64980", // pink
  "#be4bdb", // grape
  "#7950f2", // violet
];

const SIGNATURE_FONTS = [
  {
    name: "Reenie Beanie",
    label: "Playful",
    family: "'Reenie Beanie', cursive",
  },
  { name: "Ms Madi", label: "Classic", family: "'Ms Madi', cursive" },
  {
    name: "Permanent Marker",
    label: "Marker",
    family: "'Permanent Marker', cursive",
  },
];

type SignDocumentViewProps = {
  initialFile?: File | null;
  onFileLoaded?: () => void;
  onNavigate?: (tab: string, file?: File | null) => void;
};

export function SignDocumentView({
  initialFile,
  onFileLoaded,
  onNavigate,
}: SignDocumentViewProps = {}) {
  const storedKeys = getStoredKeys();

  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfArrayBuffer, setPdfArrayBuffer] = useState<ArrayBuffer | null>(
    null,
  );
  const [existingMetadata, setExistingMetadata] =
    useState<SigningMetadata | null>(null);

  const [currentPage, setCurrentPage] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [pageImages, setPageImages] = useState<string[]>([]);
  const [pdfDimensions, setPdfDimensions] = useState<
    { width: number; height: number }[]
  >([]);
  const [scale, setScale] = useState(0.8);
  const [pageThumbs, setPageThumbs] = useState<string[]>([]);

  // Signer Session State
  const [signerEmail, setSignerEmail] = useState("");
  const [sessionStarted, setSessionStarted] = useState(false);
  const [filledFields, setFilledFields] = useState<Record<string, FilledField>>(
    {},
  );

  // Modals / Flow
  const [isFieldModalOpen, setIsFieldModalOpen] = useState(false);
  const [activeField, setActiveField] = useState<FieldDefinition | null>(null);
  const [fieldValue, setFieldValue] = useState("");
  const [selectedFont, setSelectedFont] = useState(SIGNATURE_FONTS[0].name);
  const [signatureTab, setSignatureTab] = useState<string | null>("draw");

  // Final Signing State
  const [signing, setSigning] = useState(false);
  const [passphrase, setPassphrase] = useState("");
  const [availableKeys, setAvailableKeys] = useState<KeyPair[]>([]);
  const [selectedKeyId, setSelectedKeyId] = useState<string | null>(null);
  const [isFinalizeModalOpen, setIsFinalizeModalOpen] = useState(false);
  const [status, setStatus] = useState("");

  // Server Attestation State

  // Success Modal
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [lastSignedUrl, setLastSignedUrl] = useState<string | null>(null);

  // Responsive state
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [leftDrawerOpened, setLeftDrawerOpened] = useState(false);
  const [rightDrawerOpened, setRightDrawerOpened] = useState(false);
  const [highlightedFieldId, setHighlightedFieldId] = useState<string | null>(
    null,
  );

  const sigPadRef = useRef<any>(null);

  // Scroll a specific field into view and highlight it
  const scrollToField = (fieldId: string, openModal: boolean = false) => {
    const fieldElement = document.getElementById(`field-container-${fieldId}`);
    if (fieldElement) {
      fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedFieldId(fieldId);

      // Auto-clear highlight after some time or keep it?
      // User said "give it a selected field highlight", so maybe keep it until another is selected.

      if (openModal) {
        const field = existingMetadata?.fields.find((f) => f.id === fieldId);
        if (field) handleFieldClick(field);
      }
    }
  };

  const signerFields =
    existingMetadata?.fields.filter((f) => {
      if (!signerEmail) return false;
      if (f.type === "label") return false;

      // Match by email
      if (
        f.signerEmail &&
        f.signerEmail.toLowerCase() === signerEmail.toLowerCase()
      )
        return true;

      // Match by index (fallback)
      const myIndex = existingMetadata?.signers.findIndex(
        (s) => s.email.toLowerCase() === signerEmail.toLowerCase(),
      );
      if (f.signerIndex !== undefined && f.signerIndex === myIndex) return true;

      return false;
    }) || [];

  const completedCount = signerFields.filter(
    (f) => !!filledFields[f.id],
  ).length;
  const isDocFullyFilled =
    signerFields.length > 0 && completedCount === signerFields.length;

  useEffect(() => {
    WebFont.load({
      google: {
        families: SIGNATURE_FONTS.map((f) => f.name),
      },
    });

    if (isMobile) {
      setScale(0.5); // Default start smaller on mobile
    } else {
      setScale(0.8);
    }
  }, [isMobile]);

  const handlePdfChange = async (file: File | null) => {
    setPdfFile(file);
    setExistingMetadata(null);
    setPageImages([]);
    setPageThumbs([]);
    setPdfDimensions([]);
    setNumPages(0);
    setStatus("");
    setSessionStarted(false);
    setFilledFields({});

    if (file) {
      try {
        const buffer = await file.arrayBuffer();
        setPdfArrayBuffer(buffer);

        const pdf = await pdfjsLib.getDocument({ data: buffer.slice(0) })
          .promise;
        setNumPages(pdf.numPages);

        const imgs: string[] = [];
        const thumbs: string[] = [];
        const dims: { width: number; height: number }[] = [];

        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const vpThumb = page.getViewport({ scale: 0.25 });
          const canvasThumb = document.createElement("canvas");
          canvasThumb.width = vpThumb.width;
          canvasThumb.height = vpThumb.height;
          await page.render({
            canvasContext: canvasThumb.getContext("2d")!,
            viewport: vpThumb,
          }).promise;
          thumbs.push(canvasThumb.toDataURL());

          const viewport = page.getViewport({ scale: 1.5 });
          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          await page.render({
            canvasContext: canvas.getContext("2d")!,
            viewport,
          }).promise;
          imgs.push(canvas.toDataURL());
          dims.push({
            width: page.getViewport({ scale: 1.0 }).width,
            height: page.getViewport({ scale: 1.0 }).height,
          });
        }
        setPageImages(imgs);
        setPageThumbs(thumbs);
        setPdfDimensions(dims);

        const attachments = await pdf.getAttachments();
        if (attachments && attachments[SIGNING_METADATA_FILENAME]) {
          const decoder = new TextDecoder();
          const meta = JSON.parse(
            decoder.decode(attachments[SIGNING_METADATA_FILENAME].content),
          ) as SigningMetadata;
          setExistingMetadata(meta);

          // ── Pre-signing validation (toast-based) ──────────────────
          const errors: string[] = [];
          const passes: string[] = [];

          // 1. Hash check: re-hash original.pdf
          if (attachments["original.pdf"]) {
            try {
              const reHash = await sha256Hex(
                attachments["original.pdf"].content,
              );
              if (reHash === meta.documentHash) {
                passes.push("Document integrity");
              } else {
                errors.push(
                  "Document hash mismatch — file may have been tampered with",
                );
              }
            } catch {
              errors.push("Could not verify document hash");
            }
          } else {
            errors.push(
              "Original document not embedded — integrity cannot be verified",
            );
          }

          // 2. Creator signature check
          if (
            meta.creatorSignature?.signature &&
            meta.creatorSignature.publicKey
          ) {
            try {
              // v2 signs the full metadata (hash + signers + fields), v1 signed just the hash
              const creatorInput =
                meta.version >= 2
                  ? computeCreatorSigningInput(
                    meta.documentHash,
                    meta.signers,
                    meta.fields,
                  )
                  : meta.documentHash;
              const valid = await verifyData(
                creatorInput,
                meta.creatorSignature.signature,
                meta.creatorSignature.publicKey,
              );
              if (valid) {
                passes.push("Creator signature");
              } else {
                errors.push("Creator signature is invalid");
              }
            } catch {
              errors.push("Creator signature verification failed");
            }
          }

          // 3. Server attestation check
          if (
            meta.creatorSignature?.serverSignature?.signature &&
            meta.creatorSignature.serverSignature.serverKeyId
          ) {
            try {
              const { signature: sigVal, ...payloadToVerify } =
                meta.creatorSignature.serverSignature;
              const valid = await verifyServerSignature(
                payloadToVerify,
                sigVal,
                meta.creatorSignature.serverSignature.serverKeyId,
              );
              if (valid) {
                passes.push("Server attestation");
              } else {
                errors.push("Server attestation signature is invalid");
              }
            } catch {
              errors.push("Server attestation verification failed");
            }
          }

          // Show results
          if (errors.length > 0) {
            errors.forEach((err) => toast.error(err, { duration: 8000 }));
          }
          if (passes.length > 0) {
            toast.success(`Verified: ${passes.join(", ")}`);
          }
        }
      } catch (e) {
        console.error(e);
        setStatus("Failed to load PDF or metadata.");
      }
    }
  };

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey) {
        e.preventDefault();
        setScale((s: number) => {
          const delta = -e.deltaY * 0.01;
          const newScale = Math.min(3.0, Math.max(0.1, s + delta));
          return newScale;
        });
      }
    };
    window.addEventListener("wheel", handleWheel, { passive: false });
    return () => window.removeEventListener("wheel", handleWheel);
  }, []);

  // Load initial file if provided
  useEffect(() => {
    if (initialFile && !pdfFile) {
      handlePdfChange(initialFile).then(() => {
        if (onFileLoaded) {
          onFileLoaded();
        }
      });
    }
  }, [initialFile]); // eslint-disable-line react-hooks/exhaustive-deps

  const startSession = () => {
    if (!signerEmail) return;
    setSessionStarted(true);
    const signer = existingMetadata?.signers.find(
      (s) => s.email.toLowerCase() === signerEmail.toLowerCase(),
    );
    const delegationAllowed = signer?.delegationAllowed || false;
    // Find keys for this email (or allow any if delegation is allowed)
    const keys = getStoredKeys().filter(
      (k) => delegationAllowed || k.email.toLowerCase() === signerEmail.toLowerCase(),
    );
    setAvailableKeys(keys);
    if (keys.length > 0) setSelectedKeyId(keys[0].id);
  };

  const getSignerName = () => {
    const signer = existingMetadata?.signers.find(
      (s) => s.email.toLowerCase() === signerEmail.toLowerCase(),
    );
    return signer?.name || "";
  };

  const getInitials = (name: string) => {
    if (!name) return "";
    return name
      .split(" ")
      .map((r) => r.substring(0, 1))
      .join("")
      .toUpperCase();
  };

  const handleFieldClick = (field: FieldDefinition) => {
    if (field.type === "label") return;
    if (!sessionStarted) {
      alert("Please select your identity first.");
      return;
    }

    const myIndex = existingMetadata?.signers.findIndex(
      (s) => s.email.toLowerCase() === signerEmail.toLowerCase(),
    );
    if (
      field.signerEmail &&
      field.signerEmail.toLowerCase() !== signerEmail.toLowerCase()
    ) {
      alert(`This field is reserved for ${field.signerEmail}`);
      return;
    }
    if (field.signerIndex !== undefined && field.signerIndex !== myIndex) {
      alert(`This field is reserved for signer #${field.signerIndex + 1}`);
      return;
    }

    setActiveField(field);

    const now = new Date();
    const signerName = getSignerName();

    if (field.type === "date-now") {
      const options: Intl.DateTimeFormatOptions = {
        day: "numeric",
        month: "long",
        year: "numeric",
      };
      const dateStr = now.toLocaleDateString("en-GB", options);
      setFilledFields((prev) => ({
        ...prev,
        [field.id]: {
          fieldId: field.id,
          type: "date-now",
          value: dateStr,
          timestamp: now.toISOString(),
        },
      }));
    } else if (field.type === "checkbox") {
      const current = filledFields[field.id]?.value === "true";
      setFilledFields((prev) => ({
        ...prev,
        [field.id]: {
          fieldId: field.id,
          type: "checkbox",
          value: (!current).toString(),
          timestamp: now.toISOString(),
        },
      }));
    } else {
      let defaultValue = filledFields[field.id]?.value || "";
      if (defaultValue.includes("<svg") || defaultValue.startsWith("data:image")) {
        defaultValue = "";
      }
      if (!defaultValue) {
        if (field.type === "name" || field.type === "signature")
          defaultValue = signerName;
        else if (field.type === "initials")
          defaultValue = getInitials(signerName);
      }
      setFieldValue(defaultValue);
      setIsFieldModalOpen(true);
      if (field.type === "signature" || field.type === "initials") {
        setSignatureTab("draw");
      }
    }
  };

  const saveField = () => {
    if (!activeField) return;

    let finalValue = fieldValue;
    let fontFamily: string | undefined;

    if (activeField.type === "signature" || activeField.type === "initials") {
      if (signatureTab === "draw") {
        const svgEl = sigPadRef.current?.svg;
        let svg = svgEl?.outerHTML;
        if (!svg || svg.length < 100) {
          alert("Please sign on the pad first.");
          return;
        }

        // Fetch physical container dimensions to guarantee perfect aspect-ratio viewBox mapping
        const rect = svgEl?.getBoundingClientRect();
        const w = Math.round(rect?.width || svgEl?.clientWidth || 400);
        const h = Math.round(rect?.height || svgEl?.clientHeight || 150);

        // Ensure xmlns is present for rendering as data URI
        if (!svg.includes("xmlns=")) {
          svg = svg.replace("<svg", '<svg xmlns="http://www.w3.org/2000/svg"');
        }

        // Force a robust viewBox and responsive width/height properties
        // Strip any existing viewBox, width, or height to prevent collision
        svg = svg.replace(/<svg([^>]*)/, (match: string, p1: string) => {
          let attributes = p1
            .replace(/\bviewBox\s*=\s*"[^"]*"/gi, "")
            .replace(/\bwidth\s*=\s*"[^"]*"/gi, "")
            .replace(/\bheight\s*=\s*"[^"]*"/gi, "")
            .trim();
          return `<svg ${attributes} viewBox="0 0 ${w} ${h}" width="100%" height="100%"`;
        });

        finalValue = svg;
      } else {
        if (!fieldValue.trim()) {
          alert("Please enter your name/initials.");
          return;
        }
        fontFamily = SIGNATURE_FONTS.find(
          (f) => f.name === selectedFont,
        )?.family;
      }
    }

    setFilledFields((prev) => ({
      ...prev,
      [activeField.id]: {
        fieldId: activeField.id,
        type: activeField.type as any,
        value: finalValue,
        fontFamily,
      },
    }));
    setIsFieldModalOpen(false);
  };

  const handleFinalize = () => {
    setStatus("");
    setSigning(false);
    setStatus("");
    setSigning(false);

    // Filter mandatory fields that are not filled
    const missingFields = (existingMetadata?.fields || []).filter((f) => {
      // Is it mine?
      const myIndex = existingMetadata?.signers.findIndex(
        (s) => s.email.toLowerCase() === signerEmail.toLowerCase(),
      );
      const isMine =
        f.signerEmail?.toLowerCase() === signerEmail.toLowerCase() ||
        f.signerIndex === myIndex;
      if (!isMine) return false;
      if (f.optional) return false;
      if (f.type === "label") return false;
      return !filledFields[f.id];
    });

    if (missingFields.length > 0) {
      alert(
        `Please fill in all mandatory fields (${missingFields.length} remaining).`,
      );
      return;
    }

    const hasSignature = Object.values(filledFields).some(
      (f) => f.type === "signature" || f.type === "initials",
    );
    const missingSignature = missingFields.some(
      (f) => f.type === "signature" || f.type === "initials",
    );
    if (!hasSignature && missingSignature) {
      alert("Please fill in at least one signature or initials field.");
      return;
    }
    const signer = existingMetadata?.signers.find(
      (s) => s.email.toLowerCase() === signerEmail.toLowerCase(),
    );
    const delegationAllowed = signer?.delegationAllowed || false;

    const keys = getStoredKeys();
    setAvailableKeys(
      keys.filter((k) => delegationAllowed || k.email.toLowerCase() === signerEmail.toLowerCase()),
    );
    setIsFinalizeModalOpen(true);
  };

  const executeSign = async () => {
    if (!pdfArrayBuffer || !signerEmail.trim()) return;

    setSigning(true);
    try {
      // Sign the ORIGINAL document hash if available to maintain verification chain
      const documentHash =
        existingMetadata?.documentHash || (await sha256Hex(pdfArrayBuffer));

      const key = availableKeys.find((k) => k.id === selectedKeyId);
      if (!key) throw new Error("Please select a signing key");

      // 1. Request a nonce
      setStatus("Requesting signing nonce...");
      const { nonce } = await requestSigningNonce();

      // 2. Perform local PGP signing and request server attestation
      setStatus("Computing signature & requesting attestation...");
      const { serverMetadata, pgpSignature } = await requestServerAttestation(
        documentHash,
        key.email,
        key.id,
        nonce,
        filledFields,
        key.privateKeyArmored,
        key.publicKeyArmored,
        passphrase || undefined,
      );

      // We explicitly format payload exactly as it was signed for the PDF JSON metadata
      const payload = serverMetadata.clientPayload;
      const signature = pgpSignature;

      // 3. Proceed directly to finalization
      const sigData = {
        email: key.email,
        expectedEmail: signerEmail,
        keyId: key.id,
        signature,
        timestamp: payload.timestamp,
        documentHash,
        payload,
        publicKey: key.publicKeyArmored,
      };

      await finalizeSigning(sigData, serverMetadata);

      setSigning(false);
      setIsFinalizeModalOpen(false);
    } catch (e) {
      console.error(e);
      setStatus(`Error: ${e instanceof Error ? e.message : String(e)}`);
      setSigning(false);
    }
  };

  const finalizeSigning = async (sigData: any, serverMetadata: any) => {
    console.log("finalizeSigning triggered", {
      hasData: !!sigData,
      hasBuffer: !!pdfArrayBuffer,
    });
    if (!sigData || !pdfArrayBuffer) {
      console.warn(
        "Finalization aborted: Missing signature data or PDF buffer",
      );
      return;
    }

    setSigning(true);
    setStatus("Polishing authoritative document...");
    try {
      const { email, keyId, signature, timestamp, documentHash, publicKey, expectedEmail } =
        sigData;

      // 1. Identify the source document (original.pdf attachment is preferred)
      console.log("Step 1: Loading PDF for processing...");
      // 1. Identify the source document (original.pdf attachment is preferred)
      const pdfJsDoc = await pdfjsLib.getDocument({
        data: pdfArrayBuffer.slice(0),
      }).promise;
      const attachments = await pdfJsDoc.getAttachments();
      console.log(
        "Step 1.1: Attachments found:",
        Object.keys(attachments || {}),
      );
      const originalFile = attachments ? attachments["original.pdf"] : null;

      const pdfDoc = originalFile
        ? await PDFDocument.load(originalFile.content)
        : await PDFDocument.load(pdfArrayBuffer.slice(0));

      // 1.5 If we fell back to the buffer but have metadata, strip the existing Intro and Audit pages
      // to restore a "Clean" state before re-assembly.
      if (!originalFile && existingMetadata) {
        // Check if P0 is likely the Intro Page
        const p0 = pdfDoc.getPage(0);
        const { width, height } = p0.getSize();
        // Intro was inserted as [600, 400]
        if (Math.abs(width - 600) < 1 && Math.abs(height - 400) < 1) {
          console.log(
            "Detected existing Intro page. Removing it and Audit page.",
          );
          // Remove Intro (Page 0)
          pdfDoc.removePage(0);
          // Remove Audit (Last Page) - after removing P0, the count decreased by 1
          if (pdfDoc.getPageCount() > 0) {
            pdfDoc.removePage(pdfDoc.getPageCount() - 1);
          }
        }
      }

      // 2. Clear then Re-insert the Intro Page at index 0 (ONLY if not fully signed by everyone)
      const totalSigners = existingMetadata?.signers.length || 1; // 1 if new doc
      // Note: we consider it "fully signed" if the number of unique signatures + this new one >= required signers
      // We haven't appended new signature yet, so we emulate the check
      const uniqueSignersSoFar = new Set(
        existingMetadata?.signatures.map((s) => s.delegatedFrom || s.email) || [],
      );
      uniqueSignersSoFar.add(expectedEmail || email);
      const isLastSigner = uniqueSignersSoFar.size >= totalSigners;

      const theme = await loadPdfTheme(pdfDoc);

      if (!isLastSigner) {
        console.log("Step 2: Not last signer. Inserting Intro Page...");
        await generateIntroPage(pdfDoc, {
          isComplete: false,
          documentTitle: pdfFile?.name || "Untitled Document",
          existingMetadata: existingMetadata || ({ documentHash } as any),
          theme,
        });
      } else {
        console.log("Step 2: Last signer detected. Skipping Intro Page.");
      }

      // 3. Aggregate all field data for final rendering
      const allFilled: Record<string, FilledField> = {};
      // Add historical session inputs from other signers
      if (existingMetadata?.signatures) {
        existingMetadata.signatures.forEach((sig) => {
          if (sig.sessionInput) Object.assign(allFilled, sig.sessionInput);
        });
      }
      // Add current session inputs
      Object.assign(allFilled, filledFields);

      // 4. Render EVERYTHING on clean pages
      const { helvetica, customFont, customFontBold } = theme;
      const pages = pdfDoc.getPages();

      // Helper: Wrap shared tool for local usage
      const renderFieldToImageLocal = (
        filled: FilledField,
        pxW: number,
        pxH: number,
        opts?: any,
      ) => renderFieldToImage(pdfDoc, filled, pxW, pxH, opts);

      for (const field of existingMetadata?.fields || []) {
        // Fix offset:
        // Metadata page is 1-based relative to Original.
        // If Intro Inserted: Original Page 1 is at Index 1. offset = +1.
        // If No Intro: Original Page 1 is at Index 0. offset = 0.
        const introInserted = !isLastSigner; // Variable from earlier scope
        // Fix offset:
        // If we have existing metadata, the field.page values are likely relative to the signed doc (1-based, including intro).
        // e.g. Content Page 1 is Page 2.
        // If so, we need to shift them back by 1 because we are stripping/manipulating.
        // If !existingMetadata, field.page is relative to Original (Content Page 1 is Page 1).
        const fieldPageShim = existingMetadata ? field.page - 1 : field.page;
        const pageIdx = fieldPageShim - 1 + (introInserted ? 1 : 0);

        const page = pages[pageIdx];
        if (!page) continue;

        const x = field.x * (72 / 25.4);
        const y =
          page.getSize().height -
          field.y * (72 / 25.4) -
          field.height * (72 / 25.4);
        const pxW = field.width * (72 / 25.4);
        const pxH = field.height * (72 / 25.4);

        if (field.type === "label") {
          page.drawText(field.labelText || "", { x: x + 2, y: y + 2, size: 9 });
          continue;
        }

        const filled = allFilled[field.id];

        if (filled) {
          if (
            filled.type === "name" ||
            filled.type === "date" ||
            filled.type === "date-now" ||
            filled.type === "text"
          ) {
            page.drawRectangle({
              x,
              y,
              width: pxW,
              height: pxH,
              color: rgb(1, 1, 1),
            });
            // Ensure background is solid white if it's over something
            page.drawText(filled.value, {
              x: x + 4,
              y: y + 4,
              size: 10,
              font: helvetica,
            });
          } else if (filled.type === "checkbox") {
            page.drawRectangle({
              x,
              y,
              width: pxH,
              height: pxH,
              color: rgb(1, 1, 1),
              borderColor: rgb(0, 0, 0),
              borderWidth: 1,
            });
            if (filled.value === "true") {
              page.drawText("X", {
                x: x + 2,
                y: y + 2,
                size: pxH - 4,
                font: helvetica,
              });
            }
          } else if (filled.type === "signature" || filled.type === "initials") {
            // PREMIUM SIGNATURE MARK (User Provided SVG)
            const sigboxsvg = `<svg width="100%" height="100%" viewBox="0 0 418 146" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" xml:space="preserve" xmlns:serif="http://www.serif.com/" style="fill-rule:evenodd;clip-rule:evenodd;stroke-linecap:round;stroke-linejoin:round;stroke-miterlimit:1.5;">
    <g transform="matrix(1,0,0,1,-1417.564259,-2659.177153)">
        <path d="M1757.596,2661.26L1821.369,2661.26C1827.715,2661.26 1832.867,2666.412 1832.867,2672.758L1832.867,2791.139C1832.867,2797.485 1827.715,2802.637 1821.369,2802.637L1574.605,2802.637" style="fill:none;stroke:black;stroke-width:4.17px;"/>
        <path d="M1444.638,2802.637L1431.145,2802.637C1424.799,2802.637 1419.648,2797.485 1419.648,2791.139L1419.648,2672.758C1419.648,2666.412 1424.799,2661.26 1431.145,2661.26L1632.062,2661.26" style="fill:none;stroke:black;stroke-width:4.17px;"/>
    </g>
</svg>`;

            const frameImg = await renderFieldToImageLocal(
              {
                fieldId: "frame",
                type: "signature",
                value: sigboxsvg,
              } as any,
              pxW,
              pxH,
            );
            page.drawImage(frameImg, { x, y, width: pxW, height: pxH });

            const sigEntry =
              (existingMetadata?.signatures || []).find(
                (s) => s.sessionInput?.[field.id],
              ) ||
              (field.id in filledFields ? { keyId, signature, email } : null);

            if (sigEntry) {
              if (filled.type === "signature") {
                // Signature: Allocate space more intelligently
                const qrSize = pxH - 8;
                const sigWidth = pxW - qrSize - 15;

                if (sigWidth > 0) {
                  const sigImg = await renderFieldToImageLocal(
                    filled,
                    sigWidth,
                    pxH - 8,
                    { contain: true },
                  );
                  page.drawImage(sigImg, {
                    x: x + 6,
                    y: y + 4,
                    width: sigWidth,
                    height: pxH - 8,
                  });
                }

                // Identity Overlays - Centered in the user's SVG gaps
                const idText = `ID: ${sigEntry.keyId.slice(0, 8)}`;
                // Gap is roughly at 25% for ID and 75% for Signed+
                const idX = x + pxW * 0.22 - 22;
                page.drawRectangle({
                  x: idX,
                  y: y - 1,
                  width: 45,
                  height: 6,
                  color: rgb(1, 1, 1),
                });
                page.drawText(idText, {
                  x: idX + 3,
                  y: y,
                  size: 4,
                  font: helvetica,
                });

                const spX = x + pxW * 0.7 - 20;
                page.drawRectangle({
                  x: spX,
                  y: y + pxH - 1,
                  width: 40,
                  height: 6,
                  color: rgb(1, 1, 1),
                });
                page.drawText("Signed+", {
                  x: spX + 5,
                  y: y + pxH + 0.5,
                  size: 4,
                  font: helvetica,
                });

                // Cryptographic QR Anchor
                const qrData = `Signed+\ndoc:${documentHash.slice(0, 12)}\nsig:${sigEntry.signature.slice(0, 12)}`;
                const qrImage = await pdfDoc.embedPng(
                  await QRCode.toDataURL(qrData, { margin: 1, width: 80 }),
                );
                const qrX = x + pxW - qrSize - 4;
                page.drawImage(qrImage, {
                  x: qrX,
                  y: y + 4,
                  width: qrSize,
                  height: qrSize,
                });
              } else {
                // Initials: Center the content, skip QR for space
                const sigImg = await renderFieldToImageLocal(
                  filled,
                  pxW - 8,
                  pxH - 4,
                  { contain: true },
                );
                page.drawImage(sigImg, {
                  x: x + 4,
                  y: y + 2,
                  width: pxW - 8,
                  height: pxH - 4,
                });
              }
            }
          }
        } else if (!isLastSigner) {
          // Draw placeholder for unfilled fields when document is still awaiting signatures
          const signerIndex = field.signerIndex !== undefined ? field.signerIndex : 0;
          const signerColors = [
            "#228be6", // blue
            "#12b886", // teal
            "#fab005", // yellow/gold
            "#fd7e14", // orange
            "#fa5252", // red
            "#be4bdb", // grape
            "#7950f2", // violet
          ];
          const signerColor = signerColors[signerIndex % signerColors.length];
          const hexToRgb = (hex: string) => {
            const clean = hex.replace("#", "");
            const r = parseInt(clean.substring(0, 2), 16) / 255;
            const g = parseInt(clean.substring(2, 4), 16) / 255;
            const b = parseInt(clean.substring(4, 6), 16) / 255;
            return rgb(r, g, b);
          };
          const borderRgb = hexToRgb(signerColor);
          const bgRgb = rgb(0.98, 0.98, 0.99);

          const drawRoundedRect = (pPage: any, rx: number, ry: number, rw: number, rh: number, rRadius: number, fillColor: any, borderCol: any, borderW: number) => {
            const R = Math.min(rRadius, rw / 2, rh / 2);
            const path = [
              `M ${R} 0`,
              `L ${rw - R} 0`,
              `A ${R} ${R} 0 0 1 ${rw} ${R}`,
              `L ${rw} ${rh - R}`,
              `A ${R} ${R} 0 0 1 ${rw - R} ${rh}`,
              `L ${R} ${rh}`,
              `A ${R} ${R} 0 0 1 0 ${rh - R}`,
              `L 0 ${R}`,
              `A ${R} ${R} 0 0 1 ${R} 0`,
              `Z`,
            ].join(" ");
            pPage.drawSvgPath(path, {
              x: rx,
              y: ry + rh,
              color: fillColor,
              borderColor: borderCol,
              borderWidth: borderW,
            });
          };

          // Draw the rounded border placeholder
          drawRoundedRect(page, x, y, pxW, pxH, 5, bgRgb, borderRgb, 1.25);

          // Center the text label inside the placeholder
          const labelText = `${field.type.toUpperCase()} (Signer #${signerIndex + 1})`;
          const textWidth = theme.helvetica.widthOfTextAtSize(labelText, 8);
          const textHeight = 8;
          const tx = x + (pxW - textWidth) / 2;
          const ty = y + (pxH - textHeight) / 2;

          page.drawText(labelText, {
            x: tx,
            y: ty,
            size: 8,
            font: theme.helvetica,
            color: borderRgb,
          });
        }
      }

      // 5. Update Metadata & Append fresh Audit Log
      console.log("Step 5: Attaching metadata and audit log...");
      const newSigEntry: SignatureEntry = {
        email,
        keyId,
        signature,
        timestamp,
        serverSignature: serverMetadata,
        sessionInput: filledFields,
        publicKey: publicKey, // Embed the public key so offline validators don't need it pre-installed
        ...(expectedEmail && expectedEmail.toLowerCase() !== email.toLowerCase() ? { delegatedFrom: expectedEmail } : {})
      };
      const updatedSignatures = existingMetadata
        ? [
          ...existingMetadata.signatures.filter((s) => (s.delegatedFrom || s.email) !== (expectedEmail || email)),
          newSigEntry,
        ]
        : [newSigEntry];

      const meta = createSigningMetadata(
        documentHash,
        existingMetadata?.signers || [{ email }],
        existingMetadata?.fields || [],
        updatedSignatures,
      );
      await pdfDoc.attach(
        new TextEncoder().encode(JSON.stringify(meta, null, 2)),
        SIGNING_METADATA_FILENAME,
        { mimeType: "application/json", description: "Signing metadata" },
      );
      if (originalFile) {
        // Use .content from pdfjs-dist attachment
        await pdfDoc.attach(originalFile.content, "original.pdf", {
          mimeType: "application/pdf",
          description: "Original document",
        });
      } else {
        await pdfDoc.attach(pdfArrayBuffer.slice(0), "original.pdf", {
          mimeType: "application/pdf",
          description: "Original document",
        });
      }

      // 6. Generate "Certificate of Signature" Audit Page
      await generateMetaPage(
        pdfDoc,
        {
          documentHash,
          isLastSigner,
          existingMetadata,
          updatedSignatures,
        },
        {
          pdfDoc,
          ...theme,
          renderFieldToImage: (filled, pxW, pxH) =>
            renderFieldToImage(pdfDoc, filled, pxW, pxH),
        },
      );

      const pdfBytes = await pdfDoc.save();
      console.log(
        "Step 6: PDF Saved. Initiating download... Bytes:",
        pdfBytes.length,
      );
      const blob = new Blob([pdfBytes as any], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setLastSignedUrl(url);
      setSuccessModalOpen(true);

      const downloadAnchor = document.createElement("a");
      downloadAnchor.href = url;
      downloadAnchor.download = `signed-${documentHash.slice(0, 8)}.pdf`;
      document.body.appendChild(downloadAnchor);
      downloadAnchor.click();
      document.body.removeChild(downloadAnchor);

      setStatus("Success! Document finalized.");
      // Clear signing state
      // We do NOT clear session/pdf buffer immediately so user can retry if needed,
      // but maybe we should if we want a fresh start?
      // User said "Reset" implies they want to handle the file.
      // We will clear session-specifics but keep the view alive for the success modal.
      setSessionStarted(false);
      setFilledFields({});
      setSigning(false);
    } catch (e) {
      console.error("Finalization handover failed:", e);
      setStatus(
        `Finalization Error: ${e instanceof Error ? e.message : String(e)}`,
      );
      setSigning(false);
    }
  };

  /**
   * Internal component for the field navigation list
   */
  const FieldNavigatorContent = ({
    fields,
    filledFields,
    onFieldClick,
    onClearField,
  }: {
    fields: FieldDefinition[];
    filledFields: Record<string, FilledField>;
    onFieldClick: (id: string) => void;
    onClearField: (id: string) => void;
  }) => {
    if (fields.length === 0) {
      return (
        <Center mt="xl" px="md">
          <Text size="sm" c="dimmed" ta="center">
            No fields assigned to you in this document.
          </Text>
        </Center>
      );
    }

    return (
      <Stack gap="xs">
        {fields.map((f, i) => {
          const isFilled = !!filledFields[f.id];
          return (
            <Paper
              key={f.id}
              p="xs"
              withBorder
              radius="sm"
              style={{
                cursor: "pointer",
                borderColor: isFilled ? "#40c057" : "#228be6",
                background: isFilled ? "transparent" : "rgba(34,139,230,0.05)",
                transition: "all 0.2s ease",
              }}
              onClick={() => onFieldClick(f.id)}
            >
              <Group justify="space-between" wrap="nowrap">
                <Stack gap={2}>
                  <Text size="xs" fw={700} c={isFilled ? "green.7" : "blue.7"}>
                    {f.type.toUpperCase()}
                  </Text>
                  <Text size="10px" c="dimmed">
                    Page {existingMetadata ? f.page - 1 : f.page}
                  </Text>
                </Stack>
                {isFilled ? (
                  <Group gap={4}>
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      color="gray"
                      onClick={(e) => {
                        e.stopPropagation();
                        onClearField(f.id);
                      }}
                    >
                      <IconX size={12} />
                    </ActionIcon>
                    <ThemeIcon color="green" size="sm" radius="xl">
                      <IconCheck size={12} />
                    </ThemeIcon>
                  </Group>
                ) : (
                  <Badge size="xs" variant="light">
                    Required
                  </Badge>
                )}
              </Group>
            </Paper>
          );
        })}
      </Stack>
    );
  };

  if (storedKeys.length === 0) {
    return (
      <Center style={{ minHeight: "calc(100vh - 120px)", padding: "2rem" }}>
        <Paper p="xl" radius="lg" withBorder shadow="md" style={{ maxWidth: 500, textAlign: "center", background: "white" }}>
          <ThemeIcon size={72} radius="xl" color="red" variant="light" mb="md">
            <IconShieldLock size={36} />
          </ThemeIcon>
          <Title order={2} mb="sm">Key Vault & Key Setup Required</Title>
          <Text c="dimmed" size="sm" mb="xl">
            To view or sign documents, you must first initialize your secure key vault and create or import at least one PGP key. Your keys never leave your device.
          </Text>
          <Button 
            size="md" 
            fullWidth 
            onClick={() => {
              if (onNavigate) {
                onNavigate("keys");
              }
            }}
          >
            Go to Key Management
          </Button>
        </Paper>
      </Center>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        height: "100vh",
        overflow: "hidden",
      }}
    >
      {/* MOBILE LEFT DRAWER: THUMBNAILS */}
      <Drawer
        opened={leftDrawerOpened}
        onClose={() => setLeftDrawerOpened(false)}
        size="180px"
        title="Pages"
      >
        <ScrollArea h="calc(100vh - 80px)">
          <Stack gap="sm">
            {pageThumbs.map((thumb, i) => {
              const isIntro = existingMetadata && i === 0;
              const isAudit = existingMetadata && i === pageThumbs.length - 1;
              if (isIntro || isAudit) return null;
              return (
                <Paper
                  key={i}
                  withBorder
                  onClick={() => {
                    setCurrentPage(i);
                    document
                      .getElementById(`sign-page-${i}`)
                      ?.scrollIntoView({ behavior: "smooth" });
                    setLeftDrawerOpened(false);
                  }}
                  style={{
                    cursor: "pointer",
                    borderColor: currentPage === i ? "#228be6" : "transparent",
                    borderWidth: 2,
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={thumb}
                    alt={`Page ${i + 1}`}
                    style={{ width: "100%", display: "block" }}
                  />
                  <Text size="xs" ta="center" bg="gray.1">
                    Page {existingMetadata ? i : i + 1}
                  </Text>
                </Paper>
              );
            })}
          </Stack>
        </ScrollArea>
      </Drawer>

      <Drawer
        opened={rightDrawerOpened}
        position="right"
        onClose={() => setRightDrawerOpened(false)}
        title="Fields to Sign"
      >
        <ScrollArea h="calc(100vh - 80px)" p="md">
          <FieldNavigatorContent
            fields={signerFields}
            filledFields={filledFields}
            onFieldClick={(id) => {
              scrollToField(id, true);
              setRightDrawerOpened(false);
            }}
            onClearField={(id) =>
              setFilledFields((prev) => {
                const next = { ...prev };
                delete next[id];
                return next;
              })
            }
          />
        </ScrollArea>
      </Drawer>

      {!pdfFile ? (
        <div className="full-center-frame">
          <Box style={{ width: "100%", maxWidth: 650 }}>
            <div className="premium-wrapper">
              <Title order={2} mb="lg" ta="center">
                Sign Document
              </Title>
              <PdfDropzone
                onFileSelected={handlePdfChange}
                label="Upload Your Document"
                description="Everything happens in your browser. Encrypted and Secure."
              />
            </div>
          </Box>
        </div>
      ) : (
        <>
          <Box
            p="xs"
            style={{
              borderBottom: "1px solid #dee2e6",
              background: "white",
              zIndex: 10,
            }}
          >
            <Group justify="space-between" wrap="nowrap">
              <Group gap="xs" wrap="nowrap">
                {isMobile && (
                  <ActionIcon
                    variant="light"
                    color="blue"
                    onClick={() => setLeftDrawerOpened((v) => !v)}
                    size="lg"
                  >
                    <IconPageBreak size={20} />
                  </ActionIcon>
                )}
                <Stack gap={0} visibleFrom="sm">
                  <Text size="sm" fw={700} truncate maw={200}>
                    {pdfFile.name}
                  </Text>
                  <Text size="10px" c="dimmed">
                    {numPages} pages
                  </Text>
                </Stack>
              </Group>

              {existingMetadata && !sessionStarted && (
                <Group
                  gap="xs"
                  style={{ flex: 1, maxWidth: 400 }}
                  wrap="nowrap"
                >
                  <Select
                    style={{ flex: 1 }}
                    size="xs"
                    placeholder="Signing as..."
                    data={existingMetadata.signers
                      .filter(
                        (s) =>
                          !existingMetadata.signatures.some(
                            (sig) =>
                              sig.email.toLowerCase() === s.email.toLowerCase() ||
                              sig.delegatedFrom?.toLowerCase() === s.email.toLowerCase(),
                          ),
                      )
                      .map((s, i) => ({
                        value: s.email,
                        label: `${s.email} (Signer #${i + 1})`,
                      }))}
                    value={signerEmail}
                    onChange={(val) => setSignerEmail(val || "")}
                  />
                  <Button
                    size="xs"
                    disabled={!signerEmail}
                    onClick={startSession}
                  >
                    Start
                  </Button>
                </Group>
              )}

              {sessionStarted && (
                <Group gap="xs" wrap="nowrap">
                  <Badge variant="light" color="blue" visibleFrom="xs">
                    {signerEmail}
                  </Badge>
                  <Button
                    color={isDocFullyFilled ? "green" : "blue"}
                    size="sm"
                    onClick={handleFinalize}
                    leftSection={
                      isDocFullyFilled ? (
                        <IconCheck size={16} />
                      ) : (
                        <IconPencil size={16} />
                      )
                    }
                  >
                    {isMobile ? "Finalize" : "Finalize & Sign"}
                  </Button>
                  {isMobile && (
                    <ActionIcon
                      variant="light"
                      color="blue"
                      size="lg"
                      onClick={() => setRightDrawerOpened(true)}
                    >
                      <IconList size={18} />
                    </ActionIcon>
                  )}
                </Group>
              )}

              <Group gap={4} wrap="nowrap">
                <ActionIcon
                  variant="default"
                  size="sm"
                  onClick={() =>
                    setScale((s: number) => Math.max(0.2, s - 0.1))
                  }
                  aria-label="Zoom out"
                >
                  <IconMinus size={14} />
                </ActionIcon>
                {!isMobile && (
                  <Text size="xs" w={35} ta="center">
                    {Math.round(scale * 100)}%
                  </Text>
                )}
                <ActionIcon
                  variant="default"
                  size="sm"
                  onClick={() =>
                    setScale((s: number) => Math.min(2.0, s + 0.1))
                  }
                  aria-label="Zoom in"
                >
                  <IconPlus size={14} />
                </ActionIcon>
              </Group>
            </Group>
          </Box>
          <Group
            gap={0}
            style={{ flex: 1, overflow: "hidden" }}
            align="stretch"
          >
            {!isMobile && (
              <ScrollArea
                style={{
                  width: 180,
                  height: "100%",
                  borderRight: "1px solid #ddd",
                }}
                p="xs"
              >
                <Stack gap="sm">
                  {pageThumbs.map((thumb, i) => {
                    const isIntro = existingMetadata && i === 0;
                    const isAudit =
                      existingMetadata && i === pageThumbs.length - 1;
                    if (isIntro || isAudit) return null;

                    return (
                      <Paper
                        key={i}
                        withBorder
                        onClick={() => {
                          setCurrentPage(i);
                          document
                            .getElementById(`sign-page-${i}`)
                            ?.scrollIntoView({ behavior: "smooth" });
                        }}
                        style={{
                          cursor: "pointer",
                          borderColor:
                            currentPage === i ? "#228be6" : "transparent",
                          borderWidth: 2,
                          overflow: "hidden",
                        }}
                      >
                        <img
                          src={thumb}
                          alt={`Page ${i + 1}`}
                          style={{ width: "100%", display: "block" }}
                        />
                        <Text size="xs" ta="center" bg="gray.1">
                          Page {existingMetadata ? i : i + 1}
                        </Text>
                      </Paper>
                    );
                  })}
                </Stack>
              </ScrollArea>
            )}
            <ScrollArea
              style={{
                flex: 1,
                height: "100%",
                background: "#f0f0f0",
                padding: isMobile ? 10 : 20,
              }}
            >
              {pageImages.map((img, i) => {
                const isIntro = existingMetadata && i === 0;
                const isAudit = existingMetadata && i === pageImages.length - 1;
                if (isIntro || isAudit) return null;

                return (
                  <div
                    key={i}
                    id={`sign-page-${i}`}
                    style={{
                      position: "relative",
                      marginBottom: 40,
                      width: (pdfDimensions[i]?.width || 595) * scale,
                      height: (pdfDimensions[i]?.height || 842) * scale,
                      margin: "0 auto 40px auto",
                      background: "white",
                      boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                    }}
                  >
                    <img
                      src={img}
                      alt={`Page ${i + 1}`}
                      style={{ width: "100%", height: "100%" }}
                    />

                    {/* Visual fields */}
                    {existingMetadata?.fields
                      .filter((f) => {
                        return f.page === i + 1;
                      })
                      .map((field) => {
                        const pxX = field.x * (72 / 25.4) * scale;
                        const pxY = field.y * (72 / 25.4) * scale;
                        const pxW = field.width * (72 / 25.4) * scale;
                        const pxH = field.height * (72 / 25.4) * scale;

                        const isAlreadySigned =
                          existingMetadata.signatures.some(
                            (s) =>
                              (field.signerEmail &&
                                (s.email.toLowerCase() ===
                                  field.signerEmail.toLowerCase() ||
                                  s.delegatedFrom?.toLowerCase() ===
                                  field.signerEmail.toLowerCase())) ||
                              (s.sessionInput && s.sessionInput[field.id]),
                          );
                        const isFilledInSession = !!filledFields[field.id];
                        const color =
                          field.signerIndex !== undefined
                            ? SIGNER_COLORS[
                            field.signerIndex % SIGNER_COLORS.length
                            ]
                            : "#333";

                        if (isAlreadySigned) return null;

                        return (
                          <div
                            key={field.id}
                            id={`field-container-${field.id}`}
                            onClick={() => handleFieldClick(field)}
                            style={{
                              position: "absolute",
                              left: pxX,
                              top: pxY,
                              width: pxW,
                              height: pxH,
                              border: isFilledInSession
                                ? `1px solid #ddd`
                                : `2px dashed ${color}`,
                              backgroundColor: "white",
                              cursor:
                                field.type === "label" ? "default" : "pointer",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              overflow: "hidden",
                              boxShadow:
                                highlightedFieldId === field.id
                                  ? "0 0 10px 4px rgba(34, 139, 230, 0.4)"
                                  : isFilledInSession
                                    ? "0 1px 3px rgba(0,0,0,0.05)"
                                    : "none",
                              transform:
                                highlightedFieldId === field.id
                                  ? "scale(1.02)"
                                  : "scale(1)",
                              zIndex: highlightedFieldId === field.id ? 5 : 1,
                              transition:
                                "box-shadow 0.2s ease, transform 0.2s ease, outline 0.2s ease",
                              outline:
                                highlightedFieldId === field.id
                                  ? "2px solid #228be6"
                                  : "none",
                            }}
                          >
                            {isFilledInSession ? (
                              field.type === "signature" ||
                                field.type === "initials" ? (
                                filledFields[field.id].value.includes("<svg") ||
                                  filledFields[field.id].value.startsWith(
                                    "data:image",
                                  ) ? (
                                  <img
                                    src={
                                      filledFields[field.id].value.includes(
                                        "<svg",
                                      )
                                        ? `data:image/svg+xml,${encodeURIComponent(
                                          filledFields[
                                            field.id
                                          ].value.includes("xmlns=")
                                            ? filledFields[field.id].value
                                            : filledFields[
                                              field.id
                                            ].value.replace(
                                              "<svg",
                                              '<svg xmlns="http://www.w3.org/2000/svg"',
                                            ),
                                        )}`
                                        : filledFields[field.id].value
                                    }
                                    alt="Signature"
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "contain",
                                    }}
                                  />
                                ) : (
                                  <Text
                                    size="sm"
                                    fw={700}
                                    c={color}
                                    ta="center"
                                    style={{
                                      fontFamily:
                                        filledFields[field.id].fontFamily ||
                                        "inherit",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {filledFields[field.id].value}
                                  </Text>
                                )
                              ) : (
                                <Text
                                  size="xs"
                                  fw={700}
                                  c={color}
                                  ta="center"
                                  truncate
                                  style={{ width: "100%", padding: "0 2px" }}
                                >
                                  {field.type === "checkbox"
                                    ? filledFields[field.id].value === "true"
                                      ? "✓"
                                      : "□"
                                    : filledFields[field.id].value}
                                </Text>
                              )
                            ) : (
                              <Text
                                size="xs"
                                fw={700}
                                c={field.type === "label" ? "dimmed" : color}
                                ta="center"
                                truncate
                                style={{ width: "100%", padding: "0 2px" }}
                              >
                                {field.type === "label"
                                  ? field.labelText
                                  : field.type.toUpperCase()}
                              </Text>
                            )}
                          </div>
                        );
                      })}
                  </div>
                );
              })}
            </ScrollArea>

            {!isMobile && sessionStarted && (
              <ScrollArea
                style={{
                  width: 250,
                  borderLeft: "1px solid #ddd",
                  background: "white",
                }}
                p="md"
              >
                <Title order={6} mb="md">
                  Fields to Sign ({completedCount}/{signerFields.length})
                </Title>
                <FieldNavigatorContent
                  fields={signerFields}
                  filledFields={filledFields}
                  onFieldClick={(id) => scrollToField(id, true)}
                  onClearField={(id) =>
                    setFilledFields((prev) => {
                      const next = { ...prev };
                      delete next[id];
                      return next;
                    })
                  }
                />
              </ScrollArea>
            )}
          </Group>
        </>
      )}

      {/* Field Editor Modal */}
      <Modal
        opened={isFieldModalOpen}
        onClose={() => setIsFieldModalOpen(false)}
        title={`Fill ${activeField?.type} field`}
        centered
      >
        <Stack>
          {(activeField?.type === "signature" ||
            activeField?.type === "initials") && (
              <Tabs value={signatureTab} onChange={setSignatureTab}>
                <Tabs.List grow>
                  <Tabs.Tab value="draw">Draw</Tabs.Tab>
                  <Tabs.Tab value="text">Type</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="draw" pt="md">
                  <Box
                    style={{
                      border: "1px solid #ddd",
                      borderRadius: 4,
                      background: "#fafafa",
                    }}
                  >
                    <SignaturePad
                      ref={sigPadRef}
                      height={150}
                      fill="darkblue"
                      options={{
                        size: 2,
                        thinning: 0.5,
                        smoothing: 0.5,
                        streamline: 0.5,
                      }}
                    />
                  </Box>
                  <Group justify="flex-end" mt="xs">
                    <Button
                      size="xs"
                      variant="subtle"
                      onClick={() => sigPadRef.current?.clear()}
                    >
                      Clear
                    </Button>
                  </Group>
                </Tabs.Panel>

                <Tabs.Panel value="text" pt="md">
                  <TextInput
                    label={
                      activeField?.type === "initials"
                        ? "Initials to Type"
                        : "Name to Sign"
                    }
                    placeholder={
                      activeField?.type === "initials" ? "JD" : "John Hancock"
                    }
                    value={fieldValue}
                    onChange={(e) => setFieldValue(e.target.value)}
                  />
                  <Stack gap="xs" mt="md">
                    <Text size="sm">Select Style:</Text>
                    <Group gap="xs">
                      {SIGNATURE_FONTS.map((f) => (
                        <Paper
                          key={f.name}
                          p="xs"
                          withBorder
                          onClick={() => setSelectedFont(f.name)}
                          style={{
                            cursor: "pointer",
                            borderColor:
                              selectedFont === f.name ? "#228be6" : "#eee",
                            fontFamily: f.family,
                            fontSize: 20,
                          }}
                        >
                          {fieldValue || f.name}
                        </Paper>
                      ))}
                    </Group>
                  </Stack>
                </Tabs.Panel>
              </Tabs>
            )}

          {(activeField?.type === "name" || activeField?.type === "text") && (
            <TextInput
              label={activeField.type === "name" ? "Full Name" : "Enter Text"}
              value={fieldValue}
              onChange={(e) => setFieldValue(e.target.value)}
              autoFocus
            />
          )}

          {activeField?.type === "date" && (
            <TextInput
              type="date"
              label="Select Date"
              value={fieldValue}
              onChange={(e) => setFieldValue(e.target.value)}
              autoFocus
            />
          )}

          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setIsFieldModalOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={saveField}>Done</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Finalize Signing Modal */}
      <Modal
        opened={isFinalizeModalOpen}
        onClose={() => setIsFinalizeModalOpen(false)}
        title="Security Authorization"
        centered
      >
        <Stack>
          <Alert color="indigo">
            You are about to cryptographically sign{" "}
            <b>{Object.keys(filledFields).length}</b> fields as{" "}
            <b>{signerEmail}</b>. This action is permanent and will be verified
            via the Signed+ server.
          </Alert>

          {availableKeys.length === 0 ? (
            <Alert color="orange">
              You don't have any keys stored for {signerEmail}.
            </Alert>
          ) : (
            <>
              <Select
                label="Select PGP Identity"
                value={selectedKeyId}
                onChange={setSelectedKeyId}
                data={availableKeys.map((k) => ({
                  value: k.id,
                  label: k.email ? `${k.name} (${k.email})` : k.name,
                }))}
                required
              />

              {(() => {
                const selectedKey = availableKeys.find((k) => k.id === selectedKeyId);
                const isDifferentEmail =
                  selectedKey &&
                  signerEmail &&
                  selectedKey.email.toLowerCase() !== signerEmail.toLowerCase();

                return isDifferentEmail ? (
                  <Alert color="orange" title="Delegated Signing Notice">
                    You are signing using a PGP key for <b>{selectedKey.email}</b>, which does not explicitly match the invited email address (<b>{signerEmail}</b>). 
                    This is allowed because delegated signing settings are enabled for this document. Your PGP signature will be bound to <b>{selectedKey.email}</b> with an audit trail linking it back to this invitation.
                  </Alert>
                ) : null;
              })()}
            </>
          )}

          <TextInput
            label="Key Passphrase"
            type="password"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            placeholder="Required for PGP sign"
          />

          {status && (
            <Text
              size="xs"
              color={status.includes("Success") ? "green" : "red"}
            >
              {status}
            </Text>
          )}

          <Button
            color="blue"
            fullWidth
            size="lg"
            onClick={executeSign}
            loading={signing}
            disabled={!selectedKeyId}
          >
            Authorize Application of Signature
          </Button>
        </Stack>
      </Modal>

      <Modal
        opened={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        title="Document Signed & Secured"
        centered
        size="lg"
      >
        <Stack align="center" gap="md">
          <ThemeIcon size={64} radius="xl" color="green" variant="light">
            <span style={{ fontSize: 32 }}>✓</span>
          </ThemeIcon>
          <Text size="xl" fw={700}>
            Success!
          </Text>
          <Text ta="center">
            Thank you for validating your Key. Your PDF has now been downloaded
            and is ready to be sent to your recipient.
          </Text>
          <Text ta="center" size="sm" c="dimmed">
            Since you keep the file, you can choose if that's via email,
            message, USB stick, or even carrier pigeon.
          </Text>

          <Divider w="100%" />

          <Group>
            <Button
              variant="outline"
              onClick={() => {
                if (lastSignedUrl) {
                  const a = document.createElement("a");
                  a.href = lastSignedUrl;
                  a.download = `signed-document.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                }
              }}
            >
              File didn't download? Click here to try again
            </Button>
            <Button
              onClick={() => {
                setSuccessModalOpen(false);
                // Reset for next document if desired
                setPdfFile(null);
                setExistingMetadata(null);
                setPageImages([]);
                setPdfDimensions([]);
              }}
            >
              Done
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
